import { IonBackButton } from "@ionic/react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { chevronLeftIcon } from "@/lib/icons/chevron-left.icon";
import { Routes } from "@/lib/router";
import { cx } from "@/lib/style/cva.config";

/**
 * Waar de terugknop naartoe gaat, in woorden.
 *
 * Op iOS staat naast de chevron de titel van het scherm waar je vandaan komt. Dat is geen
 * versiering: het is het enige wat vertelt waar "terug" uitkomt, en dat weet je niet vanzelf als
 * een pushbericht je midden in de app heeft gezet. Deze app liet `text=""` staan en hield dus een
 * kale chevron over.
 *
 * Ionic kan die titel niet zelf ophalen -- anders dan UIKit weet het niets van het vorige scherm.
 * Maar elke aanroep geeft al een `defaultHref` mee, en dát is precies de bestemming. Vandaar deze
 * tabel: één plek waar de bestemming aan zijn naam hangt, in plaats van zeven schermen die het
 * label met de hand meesturen en na een verhuizing naar het verkeerde scherm blijven wijzen.
 *
 * Een bestemming die hier niet in staat valt terug op "Terug". Liever dat dan een label dat het
 * verkeerde belooft.
 */
const DESTINATION_TITLES: Record<string, string> = {
  [Routes.Settings()]: "routes:tabs.profile",
  [Routes.Notifications()]: "routes:notifications.title",
};

export const BackButton = forwardRef<
  HTMLIonBackButtonElement,
  React.ComponentProps<typeof IonBackButton>
>(function BackButton(props, ref) {
  const { t } = useTranslation();

  const destination = typeof props.defaultHref === "string" ? props.defaultHref : undefined;
  const titleKey = destination ? DESTINATION_TITLES[destination] : undefined;

  return (
    <IonBackButton
      ref={ref as any}
      icon={chevronLeftIcon}
      text={t(titleKey ?? "common:a11y.back")}
      {...props}
      className={cx(
        "touch-target part-icon:m-0 part-icon:size-6 part-native:size-10",
        props.className,
      )}
    />
  );
});
